import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Edit2, Trash2, Search, ChevronLeft, ChevronRight, 
  Loader2, Settings2, Tag, Hash, Type, Calendar, List,
  CheckSquare, Mail, Phone, Link, AlignLeft, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'url', label: 'URL', icon: Link },
];

const TAG_COLORS = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#F97316',
  '#F59E0B', '#22C55E', '#10B981', '#06B6D4', '#6366F1',
  '#64748B', '#78716C'
];

export default function CustomFieldsTagsManagement() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('fields');
  
  // Custom Fields State
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldSearch, setFieldSearch] = useState('');
  const [fieldPage, setFieldPage] = useState(1);
  const [fieldTotalPages, setFieldTotalPages] = useState(1);
  const [fieldTotal, setFieldTotal] = useState(0);
  const [includeInactiveFields, setIncludeInactiveFields] = useState(false);
  
  // Tags State
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagSearch, setTagSearch] = useState('');
  const [tagPage, setTagPage] = useState(1);
  const [tagTotalPages, setTagTotalPages] = useState(1);
  const [tagTotal, setTagTotal] = useState(0);
  
  // Dialog States
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Field Form
  const [fieldForm, setFieldForm] = useState({
    name: '',
    label: '',
    field_type: 'text',
    description: '',
    is_required: false,
    is_active: true,
    options: [],
    default_value: '',
    sort_order: 0
  });
  const [newOption, setNewOption] = useState('');
  
  // Tag Form
  const [tagForm, setTagForm] = useState({
    name: '',
    color: '#3B82F6',
    description: ''
  });

  // Fetch Custom Fields
  const fetchFields = useCallback(async () => {
    setFieldsLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        entity_type: 'contact',
        page: fieldPage.toString(),
        limit: '20',
        include_inactive: includeInactiveFields.toString()
      });
      if (fieldSearch) params.append('search', fieldSearch);
      
      const response = await axios.get(`${API}/master/custom-fields?${params}`);
      if (response.data.success) {
        setFields(response.data.fields);
        setFieldTotalPages(response.data.pages);
        setFieldTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      toast.error('Failed to load custom fields');
    } finally {
      setFieldsLoading(false);
    }
  }, [token, fieldPage, fieldSearch, includeInactiveFields]);

  // Fetch Tags
  const fetchTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        entity_type: 'contact',
        page: tagPage.toString(),
        limit: '50'
      });
      if (tagSearch) params.append('search', tagSearch);
      
      const response = await axios.get(`${API}/master/tags?${params}`);
      if (response.data.success) {
        setTags(response.data.tags);
        setTagTotalPages(response.data.pages);
        setTagTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setTagsLoading(false);
    }
  }, [token, tagPage, tagSearch]);

  useEffect(() => {
    if (activeTab === 'fields') {
      fetchFields();
    } else {
      fetchTags();
    }
  }, [activeTab, fetchFields, fetchTags]);

  // Field Handlers
  const handleCreateField = async () => {
    if (!fieldForm.name.trim() || !fieldForm.label.trim()) {
      toast.error('Name and label are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/master/custom-fields?token=${token}`, {
        ...fieldForm,
        entity_type: 'contact'
      });
      if (response.data.success) {
        toast.success('Custom field created');
        setShowFieldDialog(false);
        resetFieldForm();
        fetchFields();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create field');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = async () => {
    if (!fieldForm.name.trim() || !fieldForm.label.trim()) {
      toast.error('Name and label are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/master/custom-fields/${editingField.id}?token=${token}`,
        fieldForm
      );
      if (response.data.success) {
        toast.success('Custom field updated');
        setShowFieldDialog(false);
        setEditingField(null);
        resetFieldForm();
        fetchFields();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async () => {
    setSaving(true);
    try {
      const response = await axios.delete(
        `${API}/master/custom-fields/${deleteTarget.id}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Custom field deleted');
        setShowDeleteDialog(false);
        setDeleteTarget(null);
        fetchFields();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete field');
    } finally {
      setSaving(false);
    }
  };

  // Tag Handlers
  const handleCreateTag = async () => {
    if (!tagForm.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/master/tags?token=${token}`, {
        ...tagForm,
        entity_type: 'contact'
      });
      if (response.data.success) {
        toast.success('Tag created');
        setShowTagDialog(false);
        resetTagForm();
        fetchTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!tagForm.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/master/tags/${editingTag.id}?token=${token}`,
        tagForm
      );
      if (response.data.success) {
        toast.success('Tag updated');
        setShowTagDialog(false);
        setEditingTag(null);
        resetTagForm();
        fetchTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTag = async () => {
    setSaving(true);
    try {
      const response = await axios.delete(
        `${API}/master/tags/${deleteTarget.id}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Tag deleted');
        setShowDeleteDialog(false);
        setDeleteTarget(null);
        fetchTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tag');
    } finally {
      setSaving(false);
    }
  };

  // Form Helpers
  const resetFieldForm = () => {
    setFieldForm({
      name: '',
      label: '',
      field_type: 'text',
      description: '',
      is_required: false,
      is_active: true,
      options: [],
      default_value: '',
      sort_order: 0
    });
    setNewOption('');
  };

  const resetTagForm = () => {
    setTagForm({
      name: '',
      color: '#3B82F6',
      description: ''
    });
  };

  const openEditField = (field) => {
    setEditingField(field);
    setFieldForm({
      name: field.name,
      label: field.label,
      field_type: field.field_type,
      description: field.description || '',
      is_required: field.is_required,
      is_active: field.is_active,
      options: field.options || [],
      default_value: field.default_value || '',
      sort_order: field.sort_order || 0
    });
    setShowFieldDialog(true);
  };

  const openEditTag = (tag) => {
    setEditingTag(tag);
    setTagForm({
      name: tag.name,
      color: tag.color,
      description: tag.description || ''
    });
    setShowTagDialog(true);
  };

  const addOption = () => {
    if (newOption.trim() && !fieldForm.options.includes(newOption.trim())) {
      setFieldForm(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const removeOption = (option) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.filter(o => o !== option)
    }));
  };

  const getFieldTypeIcon = (type) => {
    const fieldType = FIELD_TYPES.find(f => f.value === type);
    return fieldType ? fieldType.icon : Type;
  };

  return (
    <div className="p-6 space-y-6" data-testid="custom-fields-tags-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-blue-500" />
            Custom Fields & Tags
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage custom fields and tags for contacts
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="fields" className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Custom Fields ({fieldTotal})
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Tags ({tagTotal})
          </TabsTrigger>
        </TabsList>

        {/* Custom Fields Tab */}
        <TabsContent value="fields" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search fields..."
                    value={fieldSearch}
                    onChange={(e) => { setFieldSearch(e.target.value); setFieldPage(1); }}
                    className="pl-10"
                    data-testid="field-search-input"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={includeInactiveFields}
                      onCheckedChange={setIncludeInactiveFields}
                      id="include-inactive-fields"
                    />
                    <Label htmlFor="include-inactive-fields" className="text-sm">
                      Show Inactive
                    </Label>
                  </div>
                  <Button
                    onClick={() => { resetFieldForm(); setEditingField(null); setShowFieldDialog(true); }}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="create-field-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Field
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Custom Fields</CardTitle>
              <CardDescription>
                Define additional fields to capture on contact records
              </CardDescription>
            </CardHeader>
            <CardContent>
              {fieldsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : fields.length === 0 ? (
                <div className="text-center py-12">
                  <Settings2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No custom fields found</p>
                  <p className="text-sm text-slate-400 mt-1">Create your first custom field to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Field</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Type</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600">Required</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((field) => {
                        const IconComponent = getFieldTypeIcon(field.field_type);
                        return (
                          <tr
                            key={field.id}
                            className="border-b border-slate-100 hover:bg-slate-50"
                            data-testid={`field-row-${field.id}`}
                          >
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-900">{field.label}</p>
                                <p className="text-sm text-slate-500">{field.name}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                <IconComponent className="h-3 w-3" />
                                {FIELD_TYPES.find(f => f.value === field.field_type)?.label || field.field_type}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {field.is_required ? (
                                <Badge className="bg-amber-100 text-amber-700">Required</Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge className={field.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                                {field.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => openEditField(field)}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setDeleteTarget({ ...field, type: 'field' }); setShowDeleteDialog(true); }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {fieldTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">Page {fieldPage} of {fieldTotalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setFieldPage(p => Math.max(1, p - 1))} disabled={fieldPage === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setFieldPage(p => Math.min(fieldTotalPages, p + 1))} disabled={fieldPage === fieldTotalPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search tags..."
                    value={tagSearch}
                    onChange={(e) => { setTagSearch(e.target.value); setTagPage(1); }}
                    className="pl-10"
                    data-testid="tag-search-input"
                  />
                </div>
                <Button
                  onClick={() => { resetTagForm(); setEditingTag(null); setShowTagDialog(true); }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-tag-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tag
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact Tags</CardTitle>
              <CardDescription>
                Create tags to categorize and filter contacts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tagsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : tags.length === 0 ? (
                <div className="text-center py-12">
                  <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No tags found</p>
                  <p className="text-sm text-slate-400 mt-1">Create your first tag to organize contacts</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {tags.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-slate-300 bg-white"
                      data-testid={`tag-item-${tag.id}`}
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="font-medium text-slate-700">{tag.name}</span>
                      {tag.usage_count > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {tag.usage_count}
                        </Badge>
                      )}
                      <div className="flex items-center gap-1 ml-2">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEditTag(tag)}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => { setDeleteTarget({ ...tag, type: 'tag' }); setShowDeleteDialog(true); }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tagTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">Page {tagPage} of {tagTotalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setTagPage(p => Math.max(1, p - 1))} disabled={tagPage === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setTagPage(p => Math.min(tagTotalPages, p + 1))} disabled={tagPage === tagTotalPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Custom Field Dialog */}
      <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Edit Custom Field' : 'Create Custom Field'}</DialogTitle>
            <DialogDescription>
              Define a custom field for contact records
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="field-name">Field Name *</Label>
                <Input
                  id="field-name"
                  value={fieldForm.name}
                  onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g., budget_range"
                  data-testid="field-name-input"
                />
                <p className="text-xs text-slate-500 mt-1">Internal name (no spaces)</p>
              </div>
              <div>
                <Label htmlFor="field-label">Display Label *</Label>
                <Input
                  id="field-label"
                  value={fieldForm.label}
                  onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                  placeholder="e.g., Budget Range"
                  data-testid="field-label-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="field-type">Field Type</Label>
              <Select value={fieldForm.field_type} onValueChange={(v) => setFieldForm({ ...fieldForm, field_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {fieldForm.field_type === 'dropdown' && (
              <div>
                <Label>Dropdown Options</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Add option..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                  />
                  <Button type="button" variant="outline" onClick={addOption}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {fieldForm.options.map((option) => (
                    <Badge key={option} variant="secondary" className="flex items-center gap-1">
                      {option}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeOption(option)} />
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="field-description">Description</Label>
              <Textarea
                id="field-description"
                value={fieldForm.description}
                onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="field-default">Default Value</Label>
              <Input
                id="field-default"
                value={fieldForm.default_value}
                onChange={(e) => setFieldForm({ ...fieldForm, default_value: e.target.value })}
                placeholder="Default value (optional)"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={fieldForm.is_required}
                  onCheckedChange={(checked) => setFieldForm({ ...fieldForm, is_required: checked })}
                  id="field-required"
                />
                <Label htmlFor="field-required">Required Field</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={fieldForm.is_active}
                  onCheckedChange={(checked) => setFieldForm({ ...fieldForm, is_active: checked })}
                  id="field-active"
                />
                <Label htmlFor="field-active">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldDialog(false)}>Cancel</Button>
            <Button onClick={editingField ? handleUpdateField : handleCreateField} disabled={saving} data-testid="save-field-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingField ? 'Save Changes' : 'Create Field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Create Tag'}</DialogTitle>
            <DialogDescription>
              Create a tag to categorize contacts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tag-name">Tag Name *</Label>
              <Input
                id="tag-name"
                value={tagForm.name}
                onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                placeholder="e.g., VIP, Hot Lead"
                data-testid="tag-name-input"
              />
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      tagForm.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setTagForm({ ...tagForm, color })}
                  />
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="tag-description">Description</Label>
              <Textarea
                id="tag-description"
                value={tagForm.description}
                onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTagDialog(false)}>Cancel</Button>
            <Button onClick={editingTag ? handleUpdateTag : handleCreateTag} disabled={saving} data-testid="save-tag-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingTag ? 'Save Changes' : 'Create Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.type === 'field' ? 'Custom Field' : 'Tag'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.name || deleteTarget?.label}"? This action cannot be undone.
              {deleteTarget?.type === 'tag' && deleteTarget?.usage_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  This tag is used by {deleteTarget.usage_count} contact(s) and will be removed from them.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={deleteTarget?.type === 'field' ? handleDeleteField : handleDeleteTag}
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
